'use client';

import { useCallback, useMemo } from 'react';
import { useConvexAuth } from 'convex/react';

import { api, useAction, useMutation, useQuery } from '@/lib/convex';
import type { Id } from '@/convex/_generated/dataModel';
import type {
  DownloadManagerJob,
  DownloadManagerJobStatus,
} from '@/lib/download-manager-state';
import type { ManagedDownloadDeliveryMode } from '@/lib/managed-download-delivery';
import { useConfirmedWorkspaceMember } from '@/hooks/use-confirmed-workspace-member';

type ManagerFeedJob = {
  jobId: string;
  archiveName: string;
  kind: 'asset_pack' | 'folder_zip' | 'direct_asset';
  deliveryMode: ManagedDownloadDeliveryMode;
  status: DownloadManagerJobStatus;
  progressPercent: number;
  itemCount: number;
  completedItemCount: number;
  estimatedSourceBytes?: number;
  unknownSizeItemCount?: number;
  outputSizeBytes?: number;
  createdAt: number;
  startedAt?: number;
  deliveredAt?: number;
  errorMessage?: string;
};

type DownloadUrlResult = {
  url: string;
  filename: string;
  deliveryMode: ManagedDownloadDeliveryMode;
};

type RetryDownloadResult = {
  jobId: string;
  generation: number;
};

export type DownloadManagerBackend = {
  jobs: DownloadManagerJob[];
  isLoading: boolean;
  mintDownloadUrl: (jobId: string) => Promise<DownloadUrlResult>;
  startDirectAssetDownload: (request: {
    orgThreadLinkId: string;
    filename: string;
  }) => Promise<{ jobId: string; generation: number }>;
  retryDownload: (jobId: string) => Promise<RetryDownloadResult>;
  acknowledgeDelivery: (jobId: string) => Promise<void>;
  suppressManagerJobs: (jobIds: string[]) => Promise<void>;
};

export function useDownloadManagerBackend(
  orgSlug: string
): DownloadManagerBackend {
  const { isAuthenticated } = useConvexAuth();
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    createDownloadManagerOrganizationArgs(orgSlug, isAuthenticated)
  );
  const organizationId = organization.data?._id;
  // The feed is member-only: wait for the boundary to confirm membership.
  const workspaceMemberConfirmed = useConfirmedWorkspaceMember(orgSlug);
  const feedArgs = useMemo(
    () =>
      memberOnlyDownloadArgs({
        organizationId,
        membershipConfirmed: workspaceMemberConfirmed,
      }),
    [organizationId, workspaceMemberConfirmed]
  );
  const feed = useQuery(
    api.download_jobs.queries.getDownloadManagerFeed,
    feedArgs
  );
  const mintDownloadUrl = useAction(api.download_jobs.public.mintDownloadUrl);
  const startDirectAssetDownload = useMutation(
    api.download_jobs.direct_asset.startDirectAssetDownload
  );
  const acknowledgeDelivery = useMutation(
    api.download_jobs.mutations.acknowledgeDownloadDelivery
  );
  const retryDownload = useAction(api.trigger.download_job.retryDownloadJob);
  const suppressManagerJob = useMutation(
    api.download_jobs.mutations.suppressDownloadJob
  );

  const jobs = useMemo(
    () => (feed.data ?? []).map(normalizeManagerFeedJob),
    [feed.data]
  );

  return {
    jobs,
    isLoading: organization.isPending || feed.isPending,
    mintDownloadUrl: useCallback(
      (jobId) => mintDownloadUrl({ jobId: toDownloadJobId(jobId) }),
      [mintDownloadUrl]
    ),
    startDirectAssetDownload: useCallback(
      async (request) => {
        const started = await startDirectAssetDownload({
          organizationId: organization.data?._id ?? failMissingOrganization(),
          orgThreadLinkId: toOrgThreadLinkId(request.orgThreadLinkId),
          filename: request.filename,
        });
        return { jobId: started.jobId, generation: started.generation };
      },
      [organization.data?._id, startDirectAssetDownload]
    ),
    retryDownload: useCallback(
      (jobId) => retryDownload({ jobId: toDownloadJobId(jobId) }),
      [retryDownload]
    ),
    acknowledgeDelivery: useCallback(
      async (jobId) => {
        await acknowledgeDelivery({ jobId: toDownloadJobId(jobId) });
      },
      [acknowledgeDelivery]
    ),
    suppressManagerJobs: useCallback(
      async (jobIds) => {
        await Promise.all(
          jobIds.map((jobId) =>
            suppressManagerJob({ jobId: toDownloadJobId(jobId) })
          )
        );
      },
      [suppressManagerJob]
    ),
  };
}

export function createDownloadManagerFeedArgs(
  organizationId: Id<'organizations'>
) {
  return { organizationId };
}

export function createDownloadManagerOrganizationArgs(
  orgSlug: string,
  isAuthenticated: boolean
) {
  return isAuthenticated && orgSlug ? { slug: orgSlug } : 'skip';
}

function toDownloadJobId(jobId: string): Id<'downloadJobs'> {
  return jobId as Id<'downloadJobs'>;
}

function normalizeManagerFeedJob(job: ManagerFeedJob): DownloadManagerJob {
  return {
    id: job.jobId,
    archiveName: job.archiveName,
    kind: job.kind,
    deliveryMode: job.deliveryMode,
    status: job.status,
    progress: job.progressPercent,
    itemCount: job.itemCount,
    completedItemCount: job.completedItemCount,
    estimatedSourceBytes: job.estimatedSourceBytes,
    unknownSizeItemCount: job.unknownSizeItemCount,
    outputSizeBytes: job.outputSizeBytes,
    createdAt: job.createdAt,
    startedAt: job.startedAt,
    deliveredAt: job.deliveredAt,
    errorMessage: job.errorMessage,
  };
}

function toOrgThreadLinkId(value: string): Id<'orgThreadLinks'> {
  if (!value.trim()) throw new Error('DOWNLOAD_SOURCE_NOT_FOUND');
  return value as Id<'orgThreadLinks'>;
}

function failMissingOrganization(): never {
  throw new Error('DOWNLOAD_ORGANIZATION_NOT_READY');
}

/**
 * Member-only download reads subscribe only once membership is confirmed and
 * auth is not revalidating; until then they skip (#6012 triage). The server
 * still returns the empty shape for a non-member as defense in depth.
 */
export function memberOnlyDownloadArgs(args: {
  organizationId: Id<'organizations'> | undefined;
  membershipConfirmed: boolean;
  authLoading?: boolean;
}): { organizationId: Id<'organizations'> } | 'skip' {
  return args.organizationId && args.membershipConfirmed && !args.authLoading
    ? { organizationId: args.organizationId }
    : 'skip';
}
